import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import api, { type CourseAttendanceData, type CourseAttendanceStudent } from '../services/api';
import { formatLessonTime } from '../utils/time';

// ─── Константы ────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { icon: string; color: string; label: string }> = {
  present: { icon: '✅', color: 'bg-green-50 text-green-600', label: 'Пришёл' },
  late:    { icon: '⏰', color: 'bg-amber-50 text-amber-600', label: 'Опоздал' },
  absent:  { icon: '❌', color: 'bg-red-50 text-red-600',     label: 'Не был' },
  trial:   { icon: '⭐', color: 'bg-purple-50 text-purple-600', label: 'Пробный' },
  excused: { icon: '🙏', color: 'bg-blue-50 text-blue-600',   label: 'Уважит. причина' },
};

type Period = 'all' | 'month' | 'week';
type ViewMode = 'list' | 'grid';

const PERIODS: Array<{ key: Period; label: string }> = [
  { key: 'all',   label: 'Всё время' },
  { key: 'month', label: 'Месяц' },
  { key: 'week',  label: 'Неделя' },
];

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatDateLong(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function monthKey(dateStr: string): string {
  return dateStr ? dateStr.slice(0, 7) : '';
}

function monthLabel(key: string): string {
  if (!key) return '';
  const d = new Date(key + '-01T00:00:00');
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const GRID_NAME_W = 112;   // px — закреплённая колонка имён
const GRID_DATE_W = 46;    // px — ширина колонки с отметкой

// ─── Компонент ────────────────────────────────────────────────────────────

export default function CourseAttendanceView({ courseId }: { courseId: string }) {
  const navigate = useNavigate();

  const [data, setData] = useState<CourseAttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [period, setPeriod] = useState<Period>('all');
  const [view, setView] = useState<ViewMode>('list');

  // Список: развёрнутый ученик
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Сетка: подсвеченная при свайпе строка
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const d = await api.getCourseAttendance(courseId, period);
      setData(d);
    } catch (e) {
      console.error('CourseAttendanceView: Failed to load:', e);
      setError(e instanceof Error ? e.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  }, [courseId, period]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Подсветка строки при свайпе ────────────────────────────────────────
  const startHighlight = (sid: string) => {
    // Сбрасываем старый таймер, чтобы быстрые тапы по разным строкам
    // не гасили подсветку новой строки преждевременно.
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    setHighlightedId(sid);
  };
  const stopHighlight = () => {
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    // Небольшая задержка — чтобы «тап» тоже оставил подсветку на ~1 сек
    highlightTimer.current = setTimeout(() => setHighlightedId(null), 1000);
  };

  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  // ── Группировка занятий по месяцам (для шапки сетки) ───────────────────
  const monthSpans = useMemo(() => {
    if (!data) return [] as Array<{ key: string; count: number; startIndex: number }>;
    const spans: Array<{ key: string; count: number; startIndex: number }> = [];
    for (let i = 0; i < data.lessons.length; i++) {
      const key = monthKey(data.lessons[i].date);
      const last = spans[spans.length - 1];
      if (last && last.key === key) {
        last.count += 1;
      } else {
        spans.push({ key, count: 1, startIndex: i });
      }
    }
    return spans;
  }, [data]);

  // Индекс первой даты последнего месяца (её закрепляем рядом с именами)
  const pinnedMonthStartIndex = useMemo(() => {
    if (!monthSpans.length) return -1;
    return monthSpans[monthSpans.length - 1].startIndex;
  }, [monthSpans]);

  // Последние 8 занятий для полоски в списке
  const getStudentRecent = (sid: string) => {
    if (!data) return [] as Array<{ lesson: CourseAttendanceData['lessons'][number]; status: string }>;
    return data.lessons.slice(-8).map(lesson => ({
      lesson,
      status: data.marks[lesson.id]?.[sid] || '',
    }));
  };

  // Полная история ученика (для разворота)
  const getStudentHistory = (sid: string) => {
    if (!data) return [] as Array<{ lesson: CourseAttendanceData['lessons'][number]; status: string }>;
    return data.lessons.map(lesson => ({
      lesson,
      status: data.marks[lesson.id]?.[sid] || '',
    }));
  };

  const fullName = (s: CourseAttendanceStudent) => `${s.first_name} ${s.last_name}`.trim() || '—';

  // ── Загрузка ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="space-y-2 animate-fade-in">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="skeleton h-16 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  // ── Ошибка ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="tg-card flex flex-col items-center py-10 px-6 text-center">
        <span className="text-4xl mb-3">⚠️</span>
        <p className="text-sm text-[var(--tg-theme-hint-color)]">Не удалось загрузить посещаемость</p>
        <details className="w-full mt-3">
          <summary className="text-xs text-[var(--tg-theme-hint-color)] cursor-pointer hover:opacity-70">
            🔧 Техническая информация
          </summary>
          <pre className="mt-2 text-[10px] text-left text-red-500 bg-red-50 rounded-xl p-3 overflow-auto max-h-32">
            {error}
          </pre>
        </details>
        <button onClick={load} className="mt-4 text-xs text-[var(--tg-theme-button-color)] hover:opacity-70 transition-opacity">
          Попробовать снова
        </button>
      </div>
    );
  }

  if (!data) return null;

  const lessons = data.lessons;
  const students = data.students;

  return (
    <div className="space-y-3 animate-fade-in">
      {/* ── Переключатель периода ─────────────────────────────────────── */}
      <div className="flex gap-1.5">
        {PERIODS.map(p => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
              period === p.key
                ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white shadow-md'
                : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)] hover:opacity-80'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ── Переключатель вида ────────────────────────────────────────── */}
      <div className="flex gap-1 p-0.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)]">
        {([
          { key: 'list' as ViewMode, label: '📋 Список' },
          { key: 'grid' as ViewMode, label: '🔲 Сетка' },
        ]).map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
              view === v.key
                ? 'bg-[var(--tg-theme-bg-color)] text-[var(--tg-theme-text-color)] shadow-sm'
                : 'text-[var(--tg-theme-hint-color)] hover:opacity-70'
            }`}
          >
            {v.label}
          </button>
        ))}
      </div>

      {/* ── Пустое состояние ──────────────────────────────────────────── */}
      {lessons.length === 0 && (
        <div className="flex flex-col items-center py-12 text-center">
          <span className="text-4xl mb-3">📅</span>
          <p className="text-sm font-medium text-[var(--tg-theme-text-color)] mb-1">Нет занятий за этот период</p>
          <p className="text-xs text-[var(--tg-theme-hint-color)]">
            {period === 'all' ? 'У курса пока нет занятий' : 'Попробуйте выбрать «Всё время»'}
          </p>
        </div>
      )}

      {/* ── Вид: Список ───────────────────────────────────────────────── */}
      {view === 'list' && lessons.length > 0 && students.length === 0 && (
        <div className="flex flex-col items-center py-10 text-center">
          <span className="text-4xl mb-3">👨‍🎓</span>
          <p className="text-sm font-medium text-[var(--tg-theme-text-color)] mb-1">Учеников пока нет</p>
          <p className="text-xs text-[var(--tg-theme-hint-color)]">Добавьте учеников в группы курса</p>
        </div>
      )}

      {view === 'list' && lessons.length > 0 && students.length > 0 && (
        <div className="space-y-1.5">
          {students.map(s => {
            const recent = getStudentRecent(s.id);
            const history = getStudentHistory(s.id);
            const expanded = expandedId === s.id;
            return (
              <div
                key={s.id}
                className={`tg-card !p-3.5 transition-all duration-200 ${
                  expanded ? 'ring-2 ring-[var(--tg-theme-button-color)]/20' : ''
                }`}
              >
                <button
                  onClick={() => setExpandedId(expanded ? null : s.id)}
                  className="w-full text-left flex items-center gap-3"
                >
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[var(--tg-theme-button-color)] to-[var(--tg-theme-button-color)]/70 text-white flex items-center justify-center text-sm font-bold shrink-0">
                    {s.first_name?.[0] || '?'}{s.last_name?.[0] || ''}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold text-[var(--tg-theme-text-color)] block truncate">
                      {fullName(s)}
                    </span>
                    {/* Полоска последних отметок */}
                    <div className="flex items-center gap-0.5 mt-1 overflow-hidden">
                      {recent.length === 0 && (
                        <span className="text-[10px] text-[var(--tg-theme-hint-color)]">Нет отметок</span>
                      )}
                      {recent.map((r, i) => {
                        const meta = STATUS_META[r.status];
                        return (
                          <span
                            key={`${r.lesson.id}-${i}`}
                            className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
                              meta ? meta.color : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
                            }`}
                          >
                            {meta ? meta.icon : '—'}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className={`text-[var(--tg-theme-hint-color)] shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}>
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>

                {/* Развёрнутая история */}
                {expanded && (
                  <div className="mt-3 pt-3 border-t border-[var(--tg-theme-section-separator-color)] space-y-1 animate-fade-in">
                    {history.length === 0 && (
                      <p className="text-xs text-[var(--tg-theme-hint-color)] py-2 text-center">Пока нет занятий</p>
                    )}
                    {history.map(r => {
                      const meta = STATUS_META[r.status];
                      return (
                        <div key={r.lesson.id} className="flex items-center gap-2.5 py-1.5">
                          <span className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold shrink-0 ${
                            meta ? meta.color : 'bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)]'
                          }">
                            {meta ? meta.icon : '—'}
                          </span>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium text-[var(--tg-theme-text-color)] block truncate">
                              {r.lesson.title || formatDateLong(r.lesson.date)}
                            </span>
                            <span className="text-[10px] text-[var(--tg-theme-hint-color)]">
                              {formatDateLong(r.lesson.date)} · {formatLessonTime(r.lesson)}
                            </span>
                          </div>
                          <span className="text-[10px] text-[var(--tg-theme-hint-color)] shrink-0">
                            {meta ? meta.label : 'Не отмечено'}
                          </span>
                        </div>
                      );
                    })}
                    <button
                      onClick={() => navigate(`/student/${s.id}`)}
                      className="w-full mt-2 py-2.5 rounded-xl text-xs font-medium text-[var(--tg-theme-button-color)] bg-[var(--tg-theme-secondary-bg-color)] hover:opacity-80 transition-opacity"
                    >
                      👤 Перейти в карточку ученика
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Вид: Сетка ────────────────────────────────────────────────── */}
      {view === 'grid' && lessons.length > 0 && (
        <div>
          {/* Пояснение */}
          <p className="text-[10px] text-[var(--tg-theme-hint-color)] mb-1.5 flex items-center gap-1">
            <span>👈 держи строку при свайпе — она подсветится</span>
          </p>

          <div
            className="overflow-x-auto rounded-2xl border border-[var(--tg-theme-section-separator-color)]"
            onPointerUp={stopHighlight}
            onPointerCancel={stopHighlight}
          >
            <div className="min-w-max">
              {/* ── Шапка: месяцы ─────────────────────────────────── */}
              <div className="flex border-b border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)]/50">
                {/* Закреплённая колонка имён */}
                <div className="sticky left-0 z-30 shrink-0 flex items-center px-3 py-1.5 bg-[var(--tg-theme-secondary-bg-color)]"
                  style={{ width: GRID_NAME_W }}>
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-[var(--tg-theme-hint-color)]">
                    Ученик
                  </span>
                </div>
                {monthSpans.map((span, i) => (
                  <div
                    key={span.key}
                    className={`shrink-0 flex items-center justify-center py-1.5 text-[10px] font-semibold text-[var(--tg-theme-hint-color)] ${
                      span.startIndex === pinnedMonthStartIndex ? 'sticky z-20 bg-[var(--tg-theme-secondary-bg-color)]' : ''
                    }`}
                    style={{
                      width: span.count * GRID_DATE_W,
                      left: span.startIndex === pinnedMonthStartIndex ? GRID_NAME_W : undefined,
                    }}
                  >
                    {monthLabel(span.key)}
                  </div>
                ))}
              </div>

              {/* ── Шапка: даты ───────────────────────────────────── */}
              <div className="flex border-b border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)]/30">
                <div className="sticky left-0 z-30 shrink-0 bg-[var(--tg-theme-secondary-bg-color)]" style={{ width: GRID_NAME_W }} />
                {lessons.map((lesson, i) => {
                  const isPinned = i === pinnedMonthStartIndex;
                  const isFirstOfMonth = i === 0 || monthKey(lessons[i - 1].date) !== monthKey(lesson.date);
                  return (
                    <div
                      key={lesson.id}
                      className={`shrink-0 flex items-center justify-center py-1.5 text-[10px] font-medium text-[var(--tg-theme-hint-color)] ${
                        isPinned ? 'sticky z-20 bg-[var(--tg-theme-secondary-bg-color)]' : ''
                      } ${isFirstOfMonth && !isPinned ? 'border-l border-[var(--tg-theme-section-separator-color)]' : ''} ${
                        lesson.status === 'cancelled' ? 'line-through opacity-50' : ''
                      }`}
                      style={{ width: GRID_DATE_W, left: isPinned ? GRID_NAME_W : undefined }}
                    >
                      {formatDateShort(lesson.date)}
                    </div>
                  );
                })}
              </div>

              {/* ── Строки учеников ───────────────────────────────── */}
              {students.map(s => {
                const sid = s.id;
                const highlighted = highlightedId === sid;
                return (
                  <div
                    key={sid}
                    onPointerDown={() => startHighlight(sid)}
                    className={`flex border-b border-[var(--tg-theme-section-separator-color)]/50 last:border-b-0 transition-colors duration-200 ${
                      highlighted ? 'bg-[var(--tg-theme-button-color)]/10' : ''
                    }`}
                  >
                    {/* Имя — закреплено. Подсветку накладываем отдельным слоем,
                        чтобы твёрдый фон ячейки не перекрывал заливку строки */}
                    <button
                      onClick={() => navigate(`/student/${sid}`)}
                      className="sticky left-0 z-30 shrink-0 flex items-center gap-2 px-3 bg-[var(--tg-theme-bg-color)] text-left relative"
                      style={{ width: GRID_NAME_W }}
                    >
                      <span
                        className={`absolute inset-0 pointer-events-none transition-opacity duration-200 bg-[var(--tg-theme-button-color)]/10 ${highlighted ? 'opacity-100' : 'opacity-0'}`}
                      />
                      <span className="relative w-6 h-6 rounded-lg bg-gradient-to-br from-[var(--tg-theme-button-color)] to-[var(--tg-theme-button-color)]/70 text-white flex items-center justify-center text-[9px] font-bold shrink-0">
                        {s.first_name?.[0] || '?'}{s.last_name?.[0] || ''}
                      </span>
                      <span className="relative text-xs font-medium text-[var(--tg-theme-text-color)] truncate">{fullName(s)}</span>
                    </button>

                    {/* Отметки */}
                    {lessons.map((lesson, i) => {
                      const status = data.marks[lesson.id]?.[sid] || '';
                      const meta = STATUS_META[status];
                      const isPinned = i === pinnedMonthStartIndex;
                      const isFirstOfMonth = i === 0 || monthKey(lessons[i - 1].date) !== monthKey(lesson.date);
                      return (
                        <div
                          key={lesson.id}
                          className={`shrink-0 flex items-center justify-center text-sm ${
                            isPinned ? 'sticky z-10 bg-[var(--tg-theme-bg-color)]' : ''
                          } ${isFirstOfMonth && !isPinned ? 'border-l border-[var(--tg-theme-section-separator-color)]' : ''}`}
                          style={{ width: GRID_DATE_W, left: isPinned ? GRID_NAME_W : undefined }}
                        >
                          {meta ? (
                            <span className={meta.color + ' w-8 h-8 rounded-lg flex items-center justify-center text-[12px]'} title={meta.label}>
                              {meta.icon}
                            </span>
                          ) : (
                            <span className="text-[10px] text-[var(--tg-theme-hint-color)] opacity-40">—</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}

              {/* Пусто: нет учеников */}
              {students.length === 0 && (
                <div className="py-8 text-center text-xs text-[var(--tg-theme-hint-color)]">
                  Учеников пока нет — добавьте их в группы курса
                </div>
              )}
            </div>
          </div>

          {/* ── Легенда ───────────────────────────────────────────── */}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
            {Object.entries(STATUS_META).map(([key, m]) => (
              <span key={key} className="flex items-center gap-1 text-[10px] text-[var(--tg-theme-hint-color)]">
                <span className="text-[11px]">{m.icon}</span> {m.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
